import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Modal,
  Table,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
  reviewerSearchApi,
  publisherJoinJournalsApi,
  getJournalApi,
  reviewerSearchAsPublisherApi,
} from "src/network/api";
import Pagination from "./Pagination";
import PageLayout from "../PageLayout";
import CountrySelector from "./CountrySelector";
import { toast } from "react-toastify";

const ReviewerTable = () => {
  const [filters, setFilters] = useState({
    country: "",
    department: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [countryId, setCountryId] = useState(0);
  const [reviewers, setReviewers] = useState([]);
  const [journals, setJournals] = useState([]); // State to hold journals
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 10;

  const [showModal, setShowModal] = useState(false);
  const [targetReviewer, setTargetReviewer] = useState(0);
  const [targetJournal, setTargetJournal] = useState(0);

  const handleFilterChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const fetchReviewers = async (currentPage: number) => {
    const offset = currentPage;
    setLoading(true);
    setError("");
    try {
      const response = await reviewerSearchAsPublisherApi({
        limit,
        offset,
        country: countryId || "",
        department: filters.department || "",
        name: searchQuery,
      });
      setReviewers(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      setError("Failed to fetch reviewers. Please try again later.");
      console.error("Error fetching reviewers:", err);
      setReviewers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchJournals = async () => {
    try {
      const response = await getJournalApi();
      setJournals(
        response.data?.filter((item) => {
          return item.approval_status == "approved";
        })
      );
    } catch (err) {
      console.error("Error fetching journals:", err);
    }
  };

  const handleModal = () => {
    setShowModal(false);
    setTargetJournal(0);
  };

  useEffect(() => {
    fetchReviewers(page);
    fetchJournals();
  }, [filters, page, searchQuery, countryId]);

  return (
    <PageLayout>
      <div className="container-fluid reviewer-table">
        {/* Search Card Section */}
        <div className="col-md-12 center">
          <div className="">
            <div className="row">
              {/* Search Bar */}
              <div className="col-md-4 mt-3">
                <Input
                  type="text"
                  className="search-input"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Country Selector */}
              <div className="col-md-4 mt-3">
                <CountrySelector
                  currentCountry={countryId}
                  onSelectCountryCallback={(selectedCountryId) => {
                    setCountryId(selectedCountryId);
                  }}
                />
              </div>

              {/* Department Filter */}
              <div className="col-md-4">
                <select
                  name="department"
                  id="department"
                  className="filter-input form-control"
                  value={filters.department}
                  onChange={handleFilterChange}
                >
                  <option value="">All Departments</option>
                  <option value="Literature">Literature</option>
                  <option value="AI">AI</option>
                  <option value="Quantum Mechanics">Quantum Mechanics</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-container">
          <CardHeader className="bg-primary">
            <h3 className="mb-0 text-center font-weight-bold text-white">
              Reviewers List
            </h3>
          </CardHeader>

          {loading ? (
            <p className="text-center">Loading reviewers...</p>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : reviewers.length > 0 ? (
            <div className="table">
              <Table bordered hover responsive className="mb-0 " size="sm">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Contact</th>
                    <th>Department</th>
                    <th>University</th>
                    <th>Designation</th>
                    <th>Qualification</th>
                    <th>Country</th>
                    <th>State</th>
                    <th>Research Area</th>
                    {/* <th>Date of Birth</th> */}
                    <th>Profile Link</th>
                    {/* <th>About</th> */}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewers.map((reviewer, index) => (
                    <tr key={reviewer.id}>
                      <td>{index + 1 + (page - 1) * limit}</td>
                      <td>{reviewer.name || "Unknown Name"}</td>
                      <td>{reviewer.email || "N/A"}</td>
                      <td>{reviewer.contact || "N/A"}</td>
                      <td>{reviewer.department || "Not Specified"}</td>
                      <td>{reviewer.university || "N/A"}</td>
                      <td>{reviewer.designation || "N/A"}</td>
                      <td>{reviewer.maximum_qualification || "N/A"}</td>
                      <td>{reviewer.country_name || "N/A"}</td>
                      <td>{reviewer.state_name || "N/A"}</td>
                      <td>{reviewer.research_area || "N/A"}</td>
                      {/* <td>{reviewer.dob || "N/A"}</td> */}
                      <td>
                        {reviewer?.profile_link
                          ? reviewer?.profile_link
                              .split(",")
                              .map((link, index) => {
                                return (
                                  <a
                                    className="badge badge-info"
                                    href={link}
                                    target="_blank"
                                  >
                                    View Profile{" "}
                                    {index > 0 && <span>{index + 1}</span>}{" "}
                                    <i className="fa fa-external-link"></i>
                                  </a>
                                );
                              })
                          : "N/A"}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setShowModal(true);
                            setTargetReviewer(reviewer.id);
                          }}
                        >
                          Ask to Join
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p
              style={{
                marginBottom: "80px",
                marginTop: "80px",
                textAlign: "center",
                fontSize: "18px",
                color: "#6c757d",
              }}
            >
              No reviewers found.
            </p>
          )}
        </div>
      </div>

      <Modal
        className="modal-dialog-centered"
        isOpen={showModal}
        toggle={() => setShowModal(!showModal)}
      >
        <div className="modal-header">
          <h6 className="modal-title" id="modal-title-default">
            Ask Reviewer to Join Your Journal
          </h6>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => setShowModal(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Choose a Journal</label>
            <Input
              type="select"
              onChange={(e) => setTargetJournal(parseInt(e.target.value))}
            >
              <option value={0}>Select Journal</option>
              {journals.map((journal) => (
                <option key={journal.journal_id} value={journal.journal_id}>
                  {journal.journal_name}
                </option>
              ))}
            </Input>
          </div>
        </div>
        <div className="modal-footer">
          <Button
            color="primary"
            type="button"
            disabled={targetJournal === 0 || targetReviewer === 0}
            onClick={async () => {
              try {
                const response = await publisherJoinJournalsApi(
                  targetJournal,
                  targetReviewer
                );

                if (response.status === 200 && response.data.status === 200) {
                  toast.success(response.data.message);
                } else {
                  toast.error(response.data.message);
                }
              } catch (err) {
                console.error("Error sending request:", err);
                toast.error(err.response?.data?.message ?? err.message);
              } finally {
                setShowModal(false);
              }
            }}
          >
            Send Request
          </Button>

          <Button
            className="ml-auto"
            color="link"
            data-dismiss="modal"
            type="button"
            onClick={handleModal}
          >
            Close
          </Button>
        </div>
      </Modal>
      {journals.length > 0 && (
        <div className="text-center w-100">
          <Pagination total={totalPages} page={page} setPage={setPage} />
        </div>
      )}
    </PageLayout>
  );
};

export default ReviewerTable;
